import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "space-y-5"
}
const _hoisted_2 = { class: "text-secondary border-secondary bg-secondary/5 flex flex-col gap-2.5 rounded-md border p-3.5 text-xs" }
const _hoisted_3 = { class: "grid grid-cols-2 gap-3" }
const _hoisted_4 = { class: "font-bold" }
const _hoisted_5 = { class: "grid grid-cols-2 gap-3" }
const _hoisted_6 = { class: "font-bold" }
const _hoisted_7 = { class: "grid grid-cols-2 gap-3" }
const _hoisted_8 = { class: "font-bold" }
const _hoisted_9 = { class: "flex flex-col gap-3" }
const _hoisted_10 = {
  key: 0,
  class: "flex flex-col gap-2"
}

import { InputRow, Select, Combobox } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { Voucher, FileItem, VoucherFile } from '@/types';
    import { optionsYesNo } from '@/utils/form';
    import FileUploadZone from '@/components/FileUploadZone.vue';
    import { Label } from '@/components/ui/label';
    import { useI18n } from 'vue-i18n';
    import { Alert } from '@/components/ui/alert';
    import { useVoucherApprovedAccountsData } from '@/composables/useVoucherApprovedAccountsData';
    import { computed, ref, watch, watchEffect } from 'vue';
    import { useVoucherFileData } from '@/composables/useVoucherFileData';
    import { useEconomicDimensionsData } from '@/composables/useEconomicDimensionsData';
    import axios from 'axios';
    import { formatDate, formatCurrency } from '@/utils/helpers';
    import Loading from '@/components/Loading.vue';
    import type { EconomicDimension, EconomicDimensionValue } from '@/types';

    const MAX_UPLOADS = 5;
    const MAX_SIZE = 9 * 1024 * 1024;

    
export default /*@__PURE__*/_defineComponent({
  __name: 'VoucherForm',
  props: {
    voucher: {},
    form: {}
  },
  setup(__props: any) {

    const { t } = useI18n();

    const props = __props;

    const { approvedAccounts, isPendingApprovedAccounts } = useVoucherApprovedAccountsData(
        props.voucher.company_id,
    );

    const { files, isPlaceholderDataFiles } = useVoucherFileData(props.voucher.id);

    const { dimensions, isPendingDimensions } = useEconomicDimensionsData(props.voucher.company_id);

    const enabledDimensions = computed(() => {
        if (!dimensions.value) return [];

        return dimensions.value.filter((d: EconomicDimension) => d.is_active && d.is_enabled);
    });

    const getDimensionValues = (dimension: EconomicDimension) => {
        if (!dimension.values) return [];
        return dimension.values
            .filter((v: EconomicDimensionValue) => v.is_active && v.is_enabled)
            .map((v: EconomicDimensionValue) => ({
                id: v.id,
                name: `${v.key} - ${v.name}`,
                value: v.id,
            }));
    };

    const voucherFiles = ref<FileItem[]>([]);

    watchEffect(() => {
        if (files.value?.length) {
            voucherFiles.value = files.value.map((file: any) => ({
                id: file.file_id,
                name: file.filename,
                url: file.url,
                size: file.size,
                uploading: false,
                uploaded: false,
                progress: 0,
                registered: true,
            }));
        }
    });

    watch(
        voucherFiles,
        (newFiles) => {
            const unregisteredFiles = newFiles
                .filter((file) => !file.registered)
                .map((file) => file.file)
                .filter(Boolean);

            props.form.file = unregisteredFiles;
        },
        { deep: true },
    );

    const handleFileDelete = async ({ id }: { id: number }) => {
        await axios.post(route('v2.voucher.delete'), { id: props.voucher.id, file_id: id });
    };

    // Initialize individual dimension fields from dimension_values array (full objects)
    watchEffect(() => {
        if (!enabledDimensions.value.length || !Array.isArray(props.form.dimension_values)) return;

        enabledDimensions.value.forEach((dimension: EconomicDimension) => {
            const dimensionValues = getDimensionValues(dimension);
            // Find matching dimension value object by ID
            const matchingValue = dimensionValues.find((dv: any) =>
                props.form.dimension_values.some((dvObj: any) => dvObj.id === dv.value)
            );
            // Always set the value, even if null, to ensure proper initialization
            props.form[`dimension_${dimension.id}`] = matchingValue?.value || null;
        });
    });

    // Watch individual dimension fields and sync to dimension_values array (full objects)
    watch(
        () => enabledDimensions.value.map((d: EconomicDimension) => props.form[`dimension_${d.id}`]),
        (newValues) => {
            const dimensionValueObjects: any[] = [];
            enabledDimensions.value.forEach((dimension: EconomicDimension, index: number) => {
                const value = newValues[index];
                if (value !== null && value !== undefined) {
                    // Get the full dimension value object from the dimension's values array
                    const fullDimensionValue = dimension.values?.find((v: EconomicDimensionValue) => v.id === value);
                    if (fullDimensionValue) {
                        dimensionValueObjects.push(fullDimensionValue);
                    }
                }
            });
            // Only update if the array actually changed to avoid unnecessary updates
            const currentValues = props.form.dimension_values || [];
            const currentIds = currentValues.map((v: any) => v.id).sort();
            const newIds = dimensionValueObjects.map((v: any) => v.id).sort();
            if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
                props.form.dimension_values = dimensionValueObjects;
            }
        },
        { deep: true },
    );

return (_ctx: any,_cache: any) => {
  return (_unref(isPendingDimensions))
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('vouchers.voucher_table.text')), 1),
            _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.voucher.Text), 1)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('vouchers.voucher_table.date')), 1),
            _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(formatDate)(_ctx.voucher.Date)), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('vouchers.voucher_table.amount')), 1),
            _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(formatCurrency)(_ctx.voucher.Amount)), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createVNode(_unref(InputRow), {
            form: _ctx.form,
            name: "AccountHandle_Number",
            label: "vouchers.account_handle"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Combobox), {
                modelValue: _ctx.form.AccountHandle_Number,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_ctx.form.AccountHandle_Number) = $event)),
                options: _unref(approvedAccounts),
                "label-by": "number_and_name",
                "value-by": "accountNumber",
                "show-remove-button": false,
                loading: _unref(isPendingApprovedAccounts)
              }, null, 8, ["modelValue", "options", "loading"])
            ]),
            _: 1
          }, 8, ["form"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(enabledDimensions.value, (dimension) => {
            return (_openBlock(), _createBlock(_unref(InputRow), {
              key: dimension.id,
              form: _ctx.form,
              name: `dimension_${dimension.id}`,
              label: dimension.name
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Combobox), {
                  modelValue: _ctx.form[`dimension_${dimension.id}`],
                  "onUpdate:modelValue": ($event: any) => ((_ctx.form[`dimension_${dimension.id}`]) = $event),
                  options: getDimensionValues(dimension),
                  "label-by": "name",
                  "value-by": "value",
                  "show-remove-button": false,
                  loading: _unref(isPendingDimensions)
                }, null, 8, ["modelValue", "onUpdate:modelValue", "options", "loading"])
              ]),
              _: 2
            }, 1032, ["form", "name", "label"]))
          }), 128)),
          _createVNode(_unref(InputRow), {
            form: _ctx.form,
            name: "comment",
            label: "vouchers.comment_required"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Textarea), {
                id: "comment",
                modelValue: _ctx.form.comment,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_ctx.form.comment) = $event)),
                placeholder: _ctx.$t('vouchers.comment')
              }, null, 8, ["modelValue", "placeholder"])
            ]),
            _: 1
          }, 8, ["form"]),
          _createVNode(_unref(InputRow), {
            form: _ctx.form,
            name: "no_voucher",
            label: "vouchers.lost"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Select), {
                modelValue: _ctx.form.no_voucher,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_ctx.form.no_voucher) = $event)),
                options: _unref(optionsYesNo),
                translate: ""
              }, null, 8, ["modelValue", "options"])
            ]),
            _: 1
          }, 8, ["form"]),
          (Number(_ctx.form.no_voucher) === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createVNode(_unref(Label), { for: "upload_statement" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('vouchers.upload_statement', {
                            max_uploads: MAX_UPLOADS,
                        })), 1)
                  ]),
                  _: 1
                }),
                _createVNode(FileUploadZone, {
                  modelValue: voucherFiles.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((voucherFiles).value = $event)),
                  "accepts-files": ['.pdf', '.jpg', '.jpeg', '.png'],
                  multiple: "",
                  "max-uploads": MAX_UPLOADS,
                  "max-size": MAX_SIZE,
                  "instant-upload": false,
                  "delete-file": handleFileDelete
                }, null, 8, ["modelValue"])
              ]))
            : _createCommentVNode("", true),
          (Number(_ctx.form.no_voucher) === 1)
            ? (_openBlock(), _createBlock(_unref(Alert), {
                key: 1,
                variant: "warning"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('vouchers.comment_reason_lost')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]))
}
}

})